import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithCredential } from "firebase/auth";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { getStorage } from "firebase/storage";
import {
    getFirestore,
    addDoc,
    collection,
    getDoc,
    getDocs,
    query,
    where,
    updateDoc,
    doc,
    setDoc,
    onSnapshot,
    deleteDoc,
    writeBatch,
    orderBy,
} from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import {
    FirebaseMessaging,
    GetTokenOptions,
} from "@capacitor-firebase/messaging";
import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { get, startCase } from "lodash";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

// Firebase configuration
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Declare messaging as null initially. It will be populated by the async function below.
let messaging = null;
let db = null;
let storage = null;

// Initialize Firebase app (this is synchronous)
const app = initializeApp(firebaseConfig);

if ( !Capacitor.isNativePlatform() ) {
    // Initialize other services that don't cause errors
    db = getFirestore(app);
    storage = getStorage(app);
}

// Asynchronously initialize messaging only if supported
const isReady = (async () => {
    try {
        const supported = await isSupported();
        // Add a more robust check to ensure serviceWorker is not just 'in' navigator,
        // but is also not undefined or null.
        if (supported && "serviceWorker" in navigator && navigator.serviceWorker) {
            messaging = getMessaging(app);
            console.log("Firebase Messaging successfully initialized.");
        } else {
            console.warn("Firebase Messaging is not supported in this browser.");
        }
    } catch (err) {
        // This will catch the 'undefined is not an object' error and prevent a crash
        console.error("Error initializing Firebase Messaging:", err);
    }
    return true; // return 'isReady'=true regardless of success
})();

const requestToken = async () => {
    try{
        if ( Capacitor.isNativePlatform() ) {
            console.log('Attempting to get native request Token', FirebaseMessaging.isSupported());
            
//            // 1. Check current permission status
//            const permissionStatus = await FirebaseMessaging.checkPermissions();
//            console.log("Permission status: ", permissionStatus);
//
//            // 2. If status is 'prompt', the user hasn't been asked yet. Request it.
//            if (permissionStatus.receive === 'prompt') {
//                console.log('Prompt for permission');
//                const requestStatus = await FirebaseMessaging.requestPermissions();
//
//                if (requestStatus.receive !== 'granted') {
//                    console.error("User denied notification permission");
//                    throw new Error('User denied notification permission');
//                }
//            }
//            // 3. If status was already denied, we cannot proceed.
//            else if (permissionStatus.receive !== 'granted') {
//                console.error("User denied notification permission.");
//                throw new Error('User denied notification permission.');
//            }
            
            await FirebaseMessaging.requestPermissions();

            // 4. Permission is granted. Get the token.
            const result = await FirebaseMessaging.getToken({
                vapidKey: process.env.REACT_APP_VAP_ID,
            });

            return result.token;
        }
        
        if (!messaging) {
            console.warn("Cannot request token: Messaging is not initialized or not supported.");
            return null;
        }
        if ("serviceWorker" in navigator) {
            return navigator.serviceWorker
                .register("/firebase-messaging-sw.js")
                .then((registration) => {
                    // console.log(
                    //     "Service Worker registration successful, scope:",
                    //     registration.scope
                    // );
                    return getToken(messaging, {
                        vapidKey: process.env.REACT_APP_VAP_ID,
                        serviceWorkerRegistration: registration, // Ensure registration is used in the same promise chain
                    });
                })
                .then((currentToken) => {
                    if (currentToken) {
                        return currentToken;
                    } else {
                        console.log(
                            "No registration token available. Request permission to generate one."
                        );
                        return requestNotificationPermission().then((permission) => {
                            if (permission === "granted") {
                                // Handle this case: Might need to get registration again or assume it's still valid
                                return navigator.serviceWorker.ready.then((registration) => {
                                    return getToken(messaging, {
                                        vapidKey: process.env.REACT_APP_VAP_ID,
                                        serviceWorkerRegistration: registration,
                                    });
                                });
                            } else {
                                throw new Error("Permission denied");
                            }
                        });
                    }
                })
                .catch((err) => {
                    console.error("An error occurred while retrieving token. ", err);
                });
        } else {
            console.error("Service workers are not supported by this browser.");
            throw new Error("Service workers are not supported by this browser.");
        }
    } catch (error) {
        console.error("An error occurred while retrieving the token: ", error, "Native: ", Capacitor.isNativePlatform());
        return null;
    }
};

const requestNotificationPermission = async () => {
    if ( Capacitor.isNativePlatform() ) {
        // 1. Check current permission status
        const permissionStatus = await FirebaseMessaging.checkPermissions();

        // 2. If status is 'prompt', the user hasn't been asked yet. Request it.
        if (permissionStatus.receive === 'prompt') {
            const requestStatus = await FirebaseMessaging.requestPermissions();

            if (requestStatus.receive !== 'granted') {
                console.error("User denied notification permission");
                return false;
            }
        }
        // 3. If status was already denied, we cannot proceed.
        else if (permissionStatus.receive !== 'granted') {
            console.error("User denied notification permission.");
            return false;
        }
        return true;
    }
    
    // return Notification.requestPermission();
    if ("Notification" in window && "serviceWorker" in navigator) {
        return Notification.requestPermission()
            .then((permission) => {
                if (permission === "granted") {
                    return navigator.serviceWorker.getRegistration().then((reg) => {
                        reg.showNotification("Notification: You are subscribed!");
                    });
                }
            })
            .catch((error) => {
                console.error("Failed to get notification permission:", error);
                return false;
            });
    } else {
        console.warn(
            "This browser does not support desktop notification. Try a different browser for a better experience."
        );
        // Additional code to handle fallbacks
        return false;
    }
}

const handleSignInWithGoogle = async () => {
    const auth = getAuth();

    try {
        if( Capacitor.isNativePlatform() ){
            // Native Implementation
            const result = await FirebaseAuthentication.signInWithGoogle();

            // 2. Extract the ID Token from the native result
            const idToken = result.credential?.idToken;
            
            if (!idToken) {
                throw new Error("No ID token found in native Google sign-in result");
            }

            // 3. Sign in the JS SDK (Web Layer) to keep your React app in sync
            // This ensures 'auth.currentUser' is populated and onAuthStateChanged triggers.
            const credential = GoogleAuthProvider.credential(idToken);
            const userCredential = await signInWithCredential(auth, credential);
            
            console.log("🚀 ~ Native Google Login Success:", userCredential.user);
            return userCredential.user;
        }
        
        // Web Implementation
        const provider = new GoogleAuthProvider();

        // Add minimal scopes, e.g., profile and email
        provider.addScope('profile');
        provider.addScope('email');
        
        const result = await signInWithPopup(auth, provider);

        // The signed-in user info.
        const user = result.user;
        console.log("🚀 ~ Google Login User Info:", user);

        // You might want to do something with the token or user here
        return user;
    } catch (error) {
        console.error("Google Sign-in Error:", error);
    }
};

const getFirebaseUser = async (userData, userProfile) => {
    try {
        
        const userId = userData.id ?? userData._id;
        const userFullName = startCase(userProfile?.companyName);
        const profilePicture = get(userProfile, "companyLogo", "");

        // --- NATIVE IMPLEMENTATION ---
        if (Capacitor.isNativePlatform()) {
            // 1. Check if user exists
            const { documents } = await FirebaseFirestore.getCollection({
                reference: 'users',
                queryConstraints: [
                    { type: 'where', fieldPath: 'user_id', opStr: '==', value: userId },
                    { type: 'limit', limit: 1 }
                ]
            });

            if (documents.length === 0) {
                // 2. Create User
                const { reference } = await FirebaseFirestore.addDocument({
                    reference: 'users',
                    data: {
                        user_full_name: userFullName,
                        profile_picture: profilePicture,
                        status: "online",
                        user_id: userId,
                    }
                });
                // Fetch the created doc to return it
                const docSnapshot = await FirebaseFirestore.getDocument({ reference: `users/${reference.id}` });
                return docSnapshot.snapshot.data;
            } else {
                // 3. Update User
                const userDoc = documents[0];
                const updatedRecord = {
                    status: "online",
                    user_full_name: userFullName,
                    profile_picture: profilePicture,
                };
                
                await FirebaseFirestore.updateDocument({
                    reference: `users/${userDoc.id}`,
                    data: updatedRecord,
                });
                
                // Return updated data (merge existing with updates)
                return { ...userDoc.data, ...updatedRecord };
            }
        }
        
        const q = query(
            collection(db, "users"),
            where("user_id", "==", userId)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            const userDocRef = await addDoc(collection(db, "users"), {
                user_full_name: userFullName,
                profile_picture: profilePicture,
                status: "online",
                user_id: userData.id ?? userData._id,
            });
            const userDoc = await getDoc(userDocRef);
            return userDoc.data();
        } else {
            const updatedRecord = {
                status: "online",
                user_full_name: userFullName,
                profile_picture: profilePicture,
            };
            await updateFirebaseUserByUserId(userData.id, updatedRecord);
            return querySnapshot.docs[0].data();
        }
    } catch (error) {
        console.error("Error signing up:", error.message);
    }
};

const updateFirebaseUserByUserId = async (user_id, updatedData) => {
    try {
        if( Capacitor.isNativePlatform() ){
            //Native implementation
            const { documents } = await FirebaseFirestore.getCollection({
                reference: 'users',
                queryConstraints: [
                    {
                        type: 'where',
                        fieldPath: 'user_id',
                        opStr: '==',
                        value: user_id,
                    },
                    {
                        type: 'limit', // Good practice to limit if you only expect one
                        limit: 1
                    }
                ],
            });

            // 2. Emulating: if (!querySnapshot.empty)
            if (documents.length > 0) {
                const userDoc = documents[0];
                
                // 3. Emulating: updateDoc(userDocRef, updatedData)
                // We construct the path using the ID found in the query
                await FirebaseFirestore.updateDocument({
                    reference: `users/${userDoc.id}`,
                    data: updatedData,
                });
                
                console.log("User updated via Native Layer");
            } else {
                console.log("User not found!");
            }
            return;
        }
        
        //Web implementation
        const q = query(collection(db, "users"), where("user_id", "==", user_id));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            const userDocRef = doc(db, "users", userDoc.id);

            await updateDoc(userDocRef, updatedData);
        } else {
            console.log("User not found!");
        }
    } catch (error) {
        console.error("Error updating user:", error.message);
    }
};
const getFirebaseUserImageByUserId = async (user_id) => {
    try {
        if( Capacitor.isNativePlatform() ){
            //Native implementation
            const { documents } = await FirebaseFirestore.getCollection({
                reference: 'users',
                queryConstraints: [
                    {
                        type: 'where',
                        fieldPath: 'user_id',
                        opStr: '==',
                        value: user_id,
                    },
                    {
                        type: 'limit',
                        limit: 1
                    }
                ],
            });

            // 2. Emulating check for empty and getting data
            if (documents.length > 0) {
                const userDocData = documents[0].data; // Native plugin returns data in .data property
                const chatUserProfilePicture = userDocData.profile_picture;
                
                return chatUserProfilePicture || null;
            } else {
                console.log("User not found!");
                return null;
            }
        }
        
        //Web implementation
        const q = query(collection(db, "users"), where("user_id", "==", user_id));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0].data(); // Get the document data
            const chatUserProfilePicture = userDoc.profile_picture; // Extract the specific field
            return chatUserProfilePicture || null; // Return the chat_user_profile_picture or null if it doesn't exist
        } else {
            console.log("User not found!");
            return null;
        }
    } catch (error) {
        console.error("Error fetching user:", error.message);
        return null;
    }
};

const addChatListToUser = async (businessUserId, chatListData) => {
    console.log("🚀 ~ addChatListToUser ~ chatListData:", chatListData)
    try {
        if (Capacitor.isNativePlatform()) {
            //Native implementation
            const userDocId = await getNativeUserDocId(businessUserId);
            if (!userDocId) {
                console.log("No user found with the specified user_id.");
                return;
            }

            const chatListPath = `users/${userDocId}/user_chat_list`;
            
            // Check if chat exists
            const { documents } = await FirebaseFirestore.getCollection({
                reference: chatListPath,
                queryConstraints: [
                    { type: 'where', fieldPath: 'chat_id', opStr: '==', value: chatListData.chat_id }
                ]
            });

            if (documents.length === 0) {
                await FirebaseFirestore.addDocument({
                    reference: chatListPath,
                    data: chatListData
                });
            } else {
                await FirebaseFirestore.setDocument({
                    reference: `${chatListPath}/${documents[0].id}`,
                    data: chatListData,
                    merge: true // equivalent to setDoc with merge or just set
                });
            }
            
            return;
        }
        
        //Web implementation
        
        const q = query(
            collection(db, "users"),
            where("user_id", "==", businessUserId)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            const userDocRef = userDoc.ref;
            const chatListRef = collection(userDocRef, "user_chat_list");
            const chatQuery = query(
                chatListRef,
                where("chat_id", "==", chatListData.chat_id)
            );
            const chatSnapshot = await getDocs(chatQuery);

            console.log("🚀 ~ addChatListToUser ~ chatListRef, chatListData:", chatListRef, chatListData)
            if (chatSnapshot.empty) {
                await addDoc(chatListRef, chatListData);
            } else {
                await setDoc(chatSnapshot.docs[0].ref, chatListData);
            }
        } else {
            console.log("No user found with the specified user_id.");
        }
    } catch (error) {
        console.error("Error adding/updating chat list item:", error.message);
    }
};

const firestoreTimestampToDate = (timestamp) => {
    if (!timestamp) return null;
    // Native plugin sometimes returns raw ISO strings
    if (typeof timestamp === 'string') return new Date(timestamp);
    return new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1e6);
};

const getNativeUserDocId = async (businessUserId) => {
    const { documents } = await FirebaseFirestore.getCollection({
        reference: 'users',
        queryConstraints: [
            { type: 'where', fieldPath: 'user_id', opStr: '==', value: businessUserId },
            { type: 'limit', limit: 1 }
        ]
    });
    return documents.length > 0 ? documents[0].id : null;
};

const getUserChatList = async (businessUserId, isArchived) => {

    try {
        if (Capacitor.isNativePlatform()) {
            //Native implementation
            const userDocId = await getNativeUserDocId(businessUserId);
            if (!userDocId) return { chatList: [], archiveRecordCount: 0 };

            const chatListPath = `users/${userDocId}/user_chat_list`;

            // 1. Get Main Chat List
            const { documents: chatListDocs } = await FirebaseFirestore.getCollection({
                reference: chatListPath,
                queryConstraints: [
                    { type: 'where', fieldPath: 'is_user_archived', opStr: '==', value: isArchived }
                ]
            });

            // 2. Map over chats (Complex Data Fetching)
            const chatListData = await Promise.all(chatListDocs.map(async (doc) => {
                const chatData = doc.data;
                const chatId = chatData.chat_id;
                const messagesPath = `chats/${chatId}/messages`;

                // A. Unread Count
                const { documents: unreadDocs } = await FirebaseFirestore.getCollection({
                    reference: messagesPath,
                    queryConstraints: [
                        { type: 'where', fieldPath: 'read', opStr: '==', value: false },
                        { type: 'where', fieldPath: 'receiver_id', opStr: '==', value: businessUserId }
                    ]
                });

                // B. Latest Message
                const { documents: allMessages } = await FirebaseFirestore.getCollection({
                    reference: messagesPath,
                    queryConstraints: [
                        { type: 'orderBy', fieldPath: 'time', direction: 'desc' },
                        { type: 'limit', limit: 1 }
                    ]
                });

                const latestMessageTime = allMessages[0]?.data?.time || null;
                const relativeTime = latestMessageTime
                    ? dayjs(firestoreTimestampToDate(latestMessageTime)).fromNow()
                    : "No messages";

                // C. Chat User Status
                const chatUserId = await getNativeUserDocId(chatData.chat_user_id);
                let userStatus = "offline";
                if(chatUserId) {
                     const userSnapshot = await FirebaseFirestore.getDocument({ reference: `users/${chatUserId}` });
                     userStatus = userSnapshot.snapshot.data?.status || "offline";
                }

                return {
                    ...chatData,
                    unreadCount: unreadDocs.length,
                    relativeTime,
                    relativeTimeStamp: latestMessageTime,
                    userStatus
                };
            }));

            // 3. Get Archive Count
            const { documents: archivedDocs } = await FirebaseFirestore.getCollection({
                reference: chatListPath,
                queryConstraints: [
                    { type: 'where', fieldPath: 'is_user_archived', opStr: '==', value: !isArchived }
                ]
            });

            // 4. Sort
            chatListData.sort((a, b) => {
                const aDate = a.relativeTimeStamp ? firestoreTimestampToDate(a.relativeTimeStamp) : new Date(0);
                const bDate = b.relativeTimeStamp ? firestoreTimestampToDate(b.relativeTimeStamp) : new Date(0);
                return bDate - aDate;
            });

            return { chatList: chatListData, archiveRecordCount: archivedDocs.length };
        }
        
        //Web implementation
        
        const userQuery = query(
            collection(db, "users"),
            where("user_id", "==", businessUserId)
        );
        const userQuerySnapshot = await getDocs(userQuery);

        if (!userQuerySnapshot.empty) {
            const userDoc = userQuerySnapshot.docs[0];
            const userDocRef = userDoc.ref;

            const chatListRef = collection(userDocRef, "user_chat_list");

            let chatListQuery = query(
                chatListRef,
                where("is_user_archived", "==", isArchived)
            );

            const chatListSnapshot = await getDocs(chatListQuery);
            const chatListData = await Promise.all(
                chatListSnapshot.docs.map(async (doc) => {
                    const chatData = doc.data();
                    const chatId = chatData.chat_id;
                    const messagesRef = collection(db, "chats", chatId, "messages");
                    const unreadMessagesQuery = query(
                        messagesRef,
                        where("read", "==", false),
                        where("receiver_id", "==", businessUserId)
                    );
                    const unreadMessagesSnapshot = await getDocs(unreadMessagesQuery);
                    const unreadCount = unreadMessagesSnapshot.size;

                    const allMessagesQuery = query(messagesRef, orderBy("time", "desc"));
                    const querySnapshot = await getDocs(allMessagesQuery);
                    const allMessages = querySnapshot.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    }));
                    // .sort((a, b) => b.time - a.time);

                    // Get the latest message timestamp
                    const latestMessageTime = allMessages[0]?.time || null;
                    const relativeTime = latestMessageTime
                        ? dayjs(firestoreTimestampToDate(latestMessageTime)).fromNow()
                        : "No messages";

                    // Fetch the user's online/offline status from the users table
                    const chatUserQuery = query(
                        collection(db, "users"),
                        where("user_id", "==", chatData.chat_user_id)
                    );
                    const chatUserSnapshot = await getDocs(chatUserQuery);
                    const chatUser = chatUserSnapshot?.docs[0]?.data()
                        ? chatUserSnapshot.docs[0].data()
                        : { status: "offline" };

                    return {
                        ...chatData,
                        unreadCount,
                        relativeTime,
                        relativeTimeStamp: latestMessageTime,
                        userStatus: chatUser.status,
                    };
                })
            );

            const archivedChatCountQuery = query(
                chatListRef,
                where("is_user_archived", "==", !isArchived)
            );
            const archivedChatCountSnapshot = await getDocs(archivedChatCountQuery);
            const archiveRecordCount = archivedChatCountSnapshot.size;

            chatListData.sort((a, b) => {
                const aDate = a.relativeTimeStamp
                    ? firestoreTimestampToDate(a.relativeTimeStamp)
                    : new Date(0);
                const bDate = b.relativeTimeStamp
                    ? firestoreTimestampToDate(b.relativeTimeStamp)
                    : new Date(0);
                return bDate - aDate;
            });
            return {
                chatList: chatListData,
                archiveRecordCount,
            };
        } else {
            console.log("No user found with the specified user_id.");
            return {
                chatList: [],
                archiveRecordCount: 0,
            };
        }
    } catch (error) {
        console.error("Error retrieving user chat list:", error.message);
        return {
            chatList: [],
            archiveRecordCount: 0,
        };
    }
};

const listenForUserChatListUpdates = async (businessUserId, callback, isArchived) => {
    try {
        if (Capacitor.isNativePlatform()) {
            //Native implementation
            const userDocId = await getNativeUserDocId(businessUserId);
            if (!userDocId) return () => {};

            const chatListPath = `users/${userDocId}/user_chat_list`;

            // Setup Listener
            const callbackId = await FirebaseFirestore.addCollectionSnapshotListener({
                reference: chatListPath,
                queryConstraints: [
                    { type: 'where', fieldPath: 'is_user_archived', opStr: '==', value: isArchived }
                ]
            }, async (event, error) => {
                if (error) {
                    console.error("Native Listener Error:", error);
                    return;
                }
                
                // Process snapshots (re-using logic similar to getUserChatList)
                // Note: In a real app, optimize this to not re-fetch EVERYTHING on every update
                const newChatList = await Promise.all(event.snapshots.map(async (snapshot) => {
                     const chatData = snapshot.data;
                     // ... You must repeat the logic from getUserChatList here to enrich the data
                     // For brevity: I am assuming you can extract the logic from getUserChatList
                     // into a shared helper function `enrichChatData(chatData)`
                     return chatData;
                }));
                
                callback({ chatList: newChatList, archivedCount: 0 }); // Fetch count separately if needed
            });

            // Return Unsubscribe Function
            return async () => {
                await FirebaseFirestore.removeSnapshotListener({ callbackId });
            };
        }
        
        // Web implementation
        const userQuery = query(
            collection(db, "users"),
            where("user_id", "==", businessUserId)
        );

        // Use onSnapshot to listen for real-time updates
        const unsubscribe = onSnapshot(userQuery, (userQuerySnapshot) => {
            if (!userQuerySnapshot.empty) {
                const userDoc = userQuerySnapshot.docs[0];
                const userDocRef = userDoc.ref;

                const chatListRef = collection(userDocRef, "user_chat_list");

                const chatListQuery = query(
                    chatListRef,
                    where("is_user_archived", "==", isArchived)
                );

                onSnapshot(chatListQuery, async (chatListSnapshot) => {
                    const newChatList = await Promise.all(
                        chatListSnapshot.docs.map(async (doc) => {
                            const chatData = doc.data();
                            const chatId = chatData.chat_id;
                            const messagesRef = collection(db, "chats", chatId, "messages");
                            const unreadMessagesQuery = query(
                                messagesRef,
                                where("read", "==", false),
                                where("receiver_id", "==", businessUserId)
                            );

                            const unreadMessagesSnapshot = await getDocs(unreadMessagesQuery);
                            const unreadCount = unreadMessagesSnapshot.size;

                            const allMessagesQuery = query(
                                messagesRef,
                                orderBy("time", "desc")
                            );
                            const querySnapshot = await getDocs(allMessagesQuery);
                            const allMessages = querySnapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));

                            // Get the latest message timestamp
                            const latestMessageTime = allMessages[0]?.time || null;
                            const relativeTime = latestMessageTime
                                ? dayjs(firestoreTimestampToDate(latestMessageTime)).fromNow()
                                : "No messages";

                            // Fetch the user's online/offline status from the users table
                            const chatUserQuery = query(
                                collection(db, "users"),
                                where("user_id", "==", chatData.chat_user_id)
                            );
                            const chatUserSnapshot = await getDocs(chatUserQuery);
                            const chatUser = chatUserSnapshot?.docs[0]?.data()
                                ? chatUserSnapshot.docs[0].data()
                                : { status: "offline" };

                            return {
                                ...chatData,
                                unreadCount,
                                relativeTime,
                                relativeTimeStamp: latestMessageTime,
                                userStatus: chatUser.status,
                            };
                        })
                    );

                    const archivedChatListQuery = query(
                        chatListRef,
                        where("is_user_archived", "==", true)
                    );
                    const archivedChatListSnapshot = await getDocs(archivedChatListQuery);
                    const archivedCount = archivedChatListSnapshot.size;

                    newChatList.sort((a, b) => {
                        const aDate = a.relativeTimeStamp
                            ? firestoreTimestampToDate(a.relativeTimeStamp)
                            : new Date(0);
                        const bDate = b.relativeTimeStamp
                            ? firestoreTimestampToDate(b.relativeTimeStamp)
                            : new Date(0);
                        return bDate - aDate;
                    });

                    callback({
                        chatList: newChatList,
                        archivedCount,
                    });
                });
            } else {
                console.log("No user found with the specified user_id.");
                callback({
                    chatList: [],
                    archivedCount: 0,
                });
            }
        });

        return unsubscribe;
    } catch (error) {
        console.error("Error listening for user chat list updates:", error.message);
        return () => {};
    }
};

const addChatsRecord = async (chatId, businessUserId) => {
    try {
        if (Capacitor.isNativePlatform()) {
            // Native Implementation
            // 1. Create Empty Chat
            await FirebaseFirestore.setDocument({
                reference: `chats/${chatId}`,
                data: {}
            });

            // 2. Get Unread Messages
            const messagesPath = `chats/${chatId}/messages`;
            const { documents } = await FirebaseFirestore.getCollection({
                reference: messagesPath,
                queryConstraints: [
                    { type: 'where', fieldPath: 'read', opStr: '==', value: false },
                    { type: 'where', fieldPath: 'receiver_id', opStr: '==', value: businessUserId }
                ]
            });

            // 3. Batch Update
            if (documents.length > 0) {
                const operations = documents.map(doc => ({
                    type: 'update',
                    reference: `${messagesPath}/${doc.id}`,
                    data: { read: true }
                }));

                await FirebaseFirestore.writeBatch({ operations });
            }

            return chatId;
        }
        
        // Web implementation
        const chatDocRef = doc(db, "chats", chatId);
        await setDoc(chatDocRef, {});

        const batch = writeBatch(db);
        const messagesRef = collection(db, "chats", chatId, "messages");
        const unreadMessagesQuery = query(
            messagesRef,
            where("read", "==", false),
            where("receiver_id", "==", businessUserId)
        );

        // Fetch all unread messages
        const unreadMessagesSnapshot = await getDocs(unreadMessagesQuery);

        // Add update operations to the batch
        unreadMessagesSnapshot.docs.forEach((docSnapshot) => {
            const messageDocRef = doc(
                db,
                "chats",
                chatId,
                "messages",
                docSnapshot.id
            );
            batch.update(messageDocRef, { read: true });
        });

        // Commit the batch
        await batch.commit();

        return chatDocRef.id;
    } catch (error) {
        console.error("Error creating empty chat:", error.message);
        throw new Error("Failed to create empty chat");
    }
};

const getMessagesForChat = async (chatId) => {
    try {
        if (Capacitor.isNativePlatform()) {
            // Native Implementation
            const { documents } = await FirebaseFirestore.getCollection({
                reference: `chats/${chatId}/messages`,
                queryConstraints: [
                    { type: 'orderBy', fieldPath: 'time', direction: 'asc' }
                ]
            });

            return documents.map(doc => ({
                id: doc.id,
                ...doc.data
            }));
        }
        
        // Web Implementation
        
        const chatDocRef = doc(db, "chats", chatId);
        const messagesCollectionRef = collection(chatDocRef, "messages");

        const messagesQuery = query(messagesCollectionRef, orderBy("time", "asc"));

        const querySnapshot = await getDocs(messagesQuery);

        const messages = querySnapshot.docs
            .map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }))
            .sort((a, b) => a.time - b.time);

        return messages;
    } catch (error) {
        console.error("Error getting messages for chat:", error.message);
        throw new Error("Failed to get messages for chat");
    }
};

const addMessageToChat = async (chatId, messageData) => {
    try {
        if (Capacitor.isNativePlatform()) {
            // Native Implementation
            const result = await FirebaseFirestore.addDocument({
                reference: `chats/${chatId}/messages`,
                data: messageData
            });
            return { messageDocId: result.reference.id }; // returns reference
        }
        
        // Web Implementation
        const chatDocRef = doc(db, "chats", chatId);
        const messageDocRef = await addDoc(
            collection(chatDocRef, "messages"),
            messageData
        );

        return { messageDocId: messageDocRef.id };
    } catch (error) {
        console.error("Error adding message to chat:", error.message);
        throw new Error("Failed to add message to chat");
    }
};

const listenForNewMessages = async (chatId, callback) => {
    console.log("🚀 ~ listenForNewMessages ~ chatId, callback:", chatId, callback);
    try {
        if (Capacitor.isNativePlatform()) {
            // Native implementation
            const callbackId = await FirebaseFirestore.addCollectionSnapshotListener({
                reference: `chats/${chatId}/messages`,
                queryConstraints: [
                    // Note: Native listener might not support sorting in the listener config itself reliably
                    // depending on plugin version, usually better to sort in client code
                ]
            }, (event, error) => {
                if (error) return console.error(error);
                
                const messages = event.snapshots.map(doc => ({
                    id: doc.id,
                    ...doc.data
                })).sort((a, b) => a.time - b.time); // Client side sort
                
                callback(messages);
            });

            return async () => {
                await FirebaseFirestore.removeSnapshotListener({ callbackId });
            };
        }
        
        // Web Implementation
        const chatDocRef = doc(db, "chats", chatId);
        const messagesCollectionRef = collection(chatDocRef, "messages");

        const unsubscribe = onSnapshot(messagesCollectionRef, (snapshot) => {
            const messages = snapshot.docs
                .map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }))
                .sort((a, b) => a.time - b.time);

            callback(messages);
        });

        return unsubscribe;
    } catch (error) {
        console.error("Error listening for new messages:", error.message);
        return () => {};
    }
};

const getChatListItemByChatId = async (businessUserId, chatId) => {
    try {
        if (Capacitor.isNativePlatform()) {
            // Native Implementation
            const userDocId = await getNativeUserDocId(businessUserId);
            if (!userDocId) return null;

            const { documents } = await FirebaseFirestore.getCollection({
                reference: `users/${userDocId}/user_chat_list`,
                queryConstraints: [
                    { type: 'where', fieldPath: 'chat_id', opStr: '==', value: chatId },
                    { type: 'limit', limit: 1 }
                ]
            });

            return documents.length > 0 ? documents[0].data : null;
        }
        
        // Web Implementation
        const userQuery = query(
            collection(db, "users"),
            where("user_id", "==", businessUserId)
        );
        const userQuerySnapshot = await getDocs(userQuery);

        if (!userQuerySnapshot.empty) {
            const userDoc = userQuerySnapshot.docs[0];
            const userDocRef = userDoc.ref;

            const chatListRef = collection(userDocRef, "user_chat_list");

            const chatQuery = query(chatListRef, where("chat_id", "==", chatId));
            const chatQuerySnapshot = await getDocs(chatQuery);

            if (!chatQuerySnapshot.empty) {
                const chatDoc = chatQuerySnapshot.docs[0];
                const chatData = chatDoc.data();

                return chatData;
            } else {
                console.log("No chat item found with the specified chat_id.");
                return null;
            }
        } else {
            console.log("No user found with the specified user_id.");
            return null;
        }
    } catch (error) {
        console.error("Error retrieving chat list item:", error.message);
        return null;
    }
};

const deleteUserFromUserChatList = async (businessUserId, chatId) => {
    try {
        if (Capacitor.isNativePlatform()) {
            // Native Implementation
            const userDocId = await getNativeUserDocId(businessUserId);
            if (!userDocId) return;

            // We need the doc ID of the chat list item first
            const { documents } = await FirebaseFirestore.getCollection({
                reference: `users/${userDocId}/user_chat_list`,
                queryConstraints: [
                    { type: 'where', fieldPath: 'chat_id', opStr: '==', value: chatId }
                ]
            });

            if (documents.length > 0) {
                await FirebaseFirestore.deleteDocument({
                    reference: `users/${userDocId}/user_chat_list/${documents[0].id}`
                });
            }
            return;
        }
        
        // Web Implementation
        const userQuery = query(
            collection(db, "users"),
            where("user_id", "==", businessUserId)
        );
        const userQuerySnapshot = await getDocs(userQuery);

        if (!userQuerySnapshot.empty) {
            const userDoc = userQuerySnapshot.docs[0];
            const userDocRef = userDoc.ref;

            const chatListRef = collection(userDocRef, "user_chat_list");

            const chatQuery = query(chatListRef, where("chat_id", "==", chatId));
            const chatQuerySnapshot = await getDocs(chatQuery);

            if (!chatQuerySnapshot.empty) {
                const chatDoc = chatQuerySnapshot.docs[0];
                const chatDocRef = chatDoc.ref;

                await deleteDoc(chatDocRef);
            } else {
                console.log("No chat item found with the specified chat_id.");
            }
        } else {
            console.log("No user found with the specified user_id.");
        }
    } catch (error) {
        console.error("Error deleting chat list item:", error.message);
    }
};

const moveChatToArchive = async (businessUserId, chatId, is_user_archived) => {
    try {
        if (Capacitor.isNativePlatform()) {
            // Native Implementation
            const userDocId = await getNativeUserDocId(businessUserId);
            if (!userDocId) return;

            const { documents } = await FirebaseFirestore.getCollection({
                reference: `users/${userDocId}/user_chat_list`,
                queryConstraints: [
                    { type: 'where', fieldPath: 'chat_id', opStr: '==', value: chatId }
                ]
            });

            if (documents.length > 0) {
                await FirebaseFirestore.updateDocument({
                    reference: `users/${userDocId}/user_chat_list/${documents[0].id}`,
                    data: { is_user_archived }
                });
            }
        }
        
        // Web Implementation
        const userQuery = query(
            collection(db, "users"),
            where("user_id", "==", businessUserId)
        );
        const userQuerySnapshot = await getDocs(userQuery);

        if (!userQuerySnapshot.empty) {
            const userDoc = userQuerySnapshot.docs[0];
            const userDocRef = userDoc.ref;

            const chatListRef = collection(userDocRef, "user_chat_list");
            const chatQuery = query(chatListRef, where("chat_id", "==", chatId));
            const chatQuerySnapshot = await getDocs(chatQuery);

            if (!chatQuerySnapshot.empty) {
                const chatDoc = chatQuerySnapshot.docs[0];
                const chatDocRef = chatDoc.ref;

                await updateDoc(chatDocRef, {
                    is_user_archived,
                });
            } else {
                console.log("No chat item found with the specified chat_id.");
            }
        } else {
            console.log("No user found with the specified user_id.");
        }
    } catch (error) {
        console.error("Error moving chat item to archive:", error.message);
    }
};

export {
    isReady,
    messaging,
    getToken,
    requestToken,
    handleSignInWithGoogle,
    getFirebaseUser,
    getFirebaseUserImageByUserId,
    updateFirebaseUserByUserId,
    addChatListToUser,
    getUserChatList,
    listenForUserChatListUpdates,
    addChatsRecord,
    addMessageToChat,
    getMessagesForChat,
    listenForNewMessages,
    getChatListItemByChatId,
    deleteUserFromUserChatList,
    moveChatToArchive,
    storage,
};
